import { ReactElement, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGINATION_SIZES, PAGINATION_SIZES_25 } from './table.const';
import { useHistory } from 'react-router-dom';

import {
  RfCdkTable,
  RfUiButton,
  RfUiCardTable,
  RfUiModal,
  RfUiPagination,
  RfUiSelectList,
} from 'renfe-components';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectElementsPerPage,
  makeSelectInfoUser,
  makeSelectPages,
  makeSelectPracticas,
  makeSelectPracticasAuditoriaCount,
  makeSelectPracticasAuditoriaParse,
  makeSelectPracticaSeleccionada,
  makeSelectPracticasList,
  makeSelectPracticasListCount,
  makeSelectPracticesParse,
  makeSelectSelectedPage,
  makeSelectTodasPracticasAuditoria,
} from 'store/practicas/selectors';
import {
  exportExcel,
  postAuditoriaRequest,
  postRechazarPracticaRequest,
  postReclamarPracticaRequest,
  postValidarPracticaRequest,
  setElementsPerPage,
  setSelectedPage,
  setSort,
} from 'store/practicas/actions';
import {
  resetCreatePracticeForm,
  setObservacionesInstructor,
  setPracticaAuditoriaSeleccionada,
  setPracticaRechazadaSeleccionada,
  setPracticaReclamadaSeleccionada,
  setPracticaValidadaSeleccionada,
  resetValidarPracticaRequest,
} from 'store/app/actions';
import { LiteralHeaderTable, Sort } from 'types';
import {
  makeSelectInstructorsSelected,
  makeSelectObservacionesInstructor,
  makeSelectPracticaAuditoriaSeleccionada,
  makeSelectPracticaRechazadaSeleccionada,
  makeSelectPracticaReclamadaSeleccionada,
  makeSelectPracticesValidSelect,
} from 'store/app/selectors';
import { RouteTypes } from 'consts';
import { getSortLabel } from 'utils/sortUtils';
import {
  makeSelectIsAdminGestorProfile,
  makeSelectIsFormadorProfile,
  makeSelectIsInstructorProfile,
  makeSelectIsManagerProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuario,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { getHeaderAudit } from './utils';
import './Table.scss';
import {
  instructorHoursHeaders,
  parseArrayData,
  parseReportHoursData,
} from './table-utils';
import { userProfile } from 'utils/profilesIndex';
import { hasAccess } from 'utils/listUtils';
import { isMobileOnly } from 'react-device-detect';
import {
  deleteSavedPagination,
  paginationChangeNumber,
} from 'utils/practiceUtils';
import { language } from 'utils/literals';
import { ObservationsContainer } from 'containers/observationsContainer';

import {useCan} from 'hooks/visibilidadComponentes';

const selectors = createStructuredSelector({
  practices: makeSelectPracticas(),
  practicas: makeSelectPracticasList(),
  practicesCount: makeSelectPracticasListCount(),
  practicesAuditoriaCount: makeSelectPracticasAuditoriaCount(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  practicesParse: makeSelectPracticesParse(),
  pages: makeSelectPages(),
  selectedPage: makeSelectSelectedPage(),
  elementsPerPage: makeSelectElementsPerPage(),
  todasPracticasAuditoria: makeSelectTodasPracticasAuditoria(),
  practicasAuditoriaParse: makeSelectPracticasAuditoriaParse(),
  practicasAuditoriaSeleccionadas: makeSelectPracticaAuditoriaSeleccionada(),
  practicasReclamadaSeleccionadas: makeSelectPracticaReclamadaSeleccionada(),
  practicasRechazadaSeleccionadas: makeSelectPracticaRechazadaSeleccionada(),
  practicesValidSelect: makeSelectPracticesValidSelect(),
  observaciones_instructor: makeSelectObservacionesInstructor(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  infoUser: makeSelectInfoUser(),
  perfilesUsuario: makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
  selectedInstructors: makeSelectInstructorsSelected(),
  isInstructorProfile: makeSelectIsInstructorProfile(),
  isManagerProfile: makeSelectIsManagerProfile(),
  isFormadorProfile: makeSelectIsFormadorProfile(),
  isAdminGestorProfile: makeSelectIsAdminGestorProfile()
} as any);

const getDefaultPagination = (pagination25: boolean): string =>
  pagination25 ? '25' : '10';

const getPaginationSizes = (pagination25: boolean): any[] =>
  pagination25 ? PAGINATION_SIZES_25 : PAGINATION_SIZES;

const getSessionNumber = (key: string, defaultValue: string): number =>
  parseInt(sessionStorage.getItem(`${key}-${window.location.pathname}`) ?? defaultValue);

const buildArrayData = (
  data: any,
  list: boolean,
  practicesParse: string,
  practicasAuditoriaParse: string
): string => JSON.stringify(data) || (list ? practicesParse : practicasAuditoriaParse);

const isJsonArrayEmpty = (arrayData: string): boolean =>
  !JSON.parse(arrayData).length;

const getPaginationDefaultValue = (pagination25: boolean): number =>
  pagination25 ? 25 : 10;

const buildPagination = (
  newPagination: any[],
  pagination25: boolean
): string => {
  const activeValue = Number(
    sessionStorage.getItem(`elementsPerPage-${window.location.pathname}`) ??
    getPaginationDefaultValue(pagination25)
  );

  return JSON.stringify(
    [...newPagination].map((epp) => ({
      ...epp,
      active: epp.value === activeValue ? 'true' : 'false',
    }))
  );
};

const hasSelectedItems = (selection: string): boolean => selection !== '';

const shouldDisableReject = (
  selected: string,
  practicas: any[],
  isAdminGestorProfile: boolean,
  isFormadorProfile: boolean
): boolean => {
  if (!hasSelectedItems(selected)) {
    return true;
  }

  const estados = practicas.map((el) => el.ID_EST_PRACTICA);
  const hasValidada = estados.includes('VA');
  const hasReclamada = estados.includes('RE');
  const hasPendienteValidar = estados.includes('PV');
  const hasPendienteCorreccion = estados.includes('PC');
  const hasAnulada = estados.includes('AN');

  if (isAdminGestorProfile) {
    return !hasValidada || hasPendienteCorreccion || hasReclamada || hasPendienteValidar || hasAnulada;
  }

  if (isFormadorProfile) {
    return !(hasValidada || hasReclamada || hasPendienteValidar) || hasPendienteCorreccion || hasAnulada;
  }

  return true;
};

const shouldDisableValid = (
  selected: string,
  practicas: any[]
): boolean => {
  if (!hasSelectedItems(selected)) {
    return true;
  }

  const estadosSeleccionados = practicas
    .filter((el) => selected.includes(el.ID_PRACTICA))
    .map((el) => el.ID_EST_PRACTICA);

  return !(estadosSeleccionados.includes('RE') || estadosSeleccionados.includes('PV'));
};

const getHeaderTable = (
  isReportHours: boolean,
  headers: LiteralHeaderTable[] | undefined,
  literalsHeaderTable: LiteralHeaderTable[]
): string => {
  const headerData = headers || literalsHeaderTable;

  return isReportHours
    ? JSON.stringify(instructorHoursHeaders(headerData))
    : JSON.stringify(headerData);
};

const getBodyTable = (
  isReportHours: boolean,
  arrayData: string,
  list: boolean,
  hideAudit: boolean,
  canSeeUserInfo: boolean
): string => {
  if (isReportHours) {
    return parseReportHoursData(arrayData);
  }

  if (list) {
    return parseArrayData(arrayData, true, canSeeUserInfo);
  }

  if (hideAudit) {
    return arrayData;
  }

  return parseArrayData(arrayData, false);
};

const getResponsiveBlockClass = (): string =>
  isMobileOnly ? 'rf-block--mobile' : '';

const getResponsiveTextClass = (): string =>
  isMobileOnly ? 'rf-block--mobile rf-text-align-center--mobile' : '';

const getVisiblePages = (): string =>
  window.innerWidth <= 1023 ? '3' : '5';

const shouldShowPaginator = (
  arrayDataIsEmpty: boolean,
  pages: number,
  handlePage: number
): boolean => !arrayDataIsEmpty && (pages > 1 || handlePage > 1);

const shouldShowMainPagination = (
  handleFilter: any,
  pages: number
): boolean => !handleFilter && Number.isInteger(pages);

const getSelectAllTitle = (dataCount: number, isTrue: boolean): boolean =>
  dataCount !== 0 && isTrue;

const getOrderValue = (dataCount: number, orderMobile: boolean): boolean =>
  dataCount !== 0 && orderMobile;

const getCalculatedPages = (dataCount: number, elementsPerPage: number): number =>
  dataCount ? Math.ceil(dataCount / elementsPerPage) : 0;

const getActivePageFromSavedState = (pages: number, savedPageActiveNumber: number): number => {
  if (!pages) {
    return 0;
  }

  return savedPageActiveNumber !== 0 ? savedPageActiveNumber : 1;
};

const parseFilter = (filter) => {
  const elemInicio =
    filter.elementsPerPage * filter.page - filter.elementsPerPage;
  const elemFin = filter.elementsPerPage * filter.page;

  return {
    orden: filter.sortBy,
    ascDesc: filter.sortingSort,
    elemInicio,
    elemFin,
    elemFinal: elemFin, // este se repita porque control los filtros desde el componente
    // pero hay peticiones que cambian el nombre de este parámetro
    // envío los dos hasta que el API se estandarice
  };
};

const resetSelectedPractices = (
  dispatch: any,
  practicasAuditoriaSeleccionadas: string,
  practicasReclamadaSeleccionadas: string,
  practicasRechazadaSeleccionadas: string,
  practicesValidSelect: string
): void => {
  if (practicasAuditoriaSeleccionadas !== '') {
    dispatch(setPracticaAuditoriaSeleccionada(''));
  }

  if (practicasReclamadaSeleccionadas !== '') {
    dispatch(setPracticaReclamadaSeleccionada(''));
  }

  if (practicasRechazadaSeleccionadas !== '') {
    dispatch(setPracticaRechazadaSeleccionada(''));
  }

  if (practicesValidSelect !== '') {
    dispatch(setPracticaValidadaSeleccionada(''));
  }
};

const navigateToAuditAndHome = (history: any): void => {
  history.push(RouteTypes.AUDIT);
  history.push(RouteTypes.HOME);
};

const navigateToHomeAndAudit = (history: any): void => {
  history.push(RouteTypes.HOME);
  history.push(RouteTypes.AUDIT);
};

const TableActions = ({
  canSeePerfil,
  enableAudit,
  enableClaim,
  enableValid,
  enableReject,
  handleExportToExcel,
  auditarSeleccion,
  openModalCerrado,
  openModalCerradoValidar,
  openModalRechazarCerrado,
  hideAudit,
  hideClaim,
  hideValid,
  hideReject,
  list,
}: any): ReactElement => {
  const showAuditButton = canSeePerfil && !list && !hideAudit;
  const showClaimButton = canSeePerfil && list && !hideClaim;
  const showValidButton = canSeePerfil && list && !hideValid;
  const showRejectButton = canSeePerfil && list && !hideReject;

  return (
    <div
      className={`rf-flex rf-justify-end rf-align-center rf-text-align-right
        ${getResponsiveTextClass()}`}
    >
      <RfUiButton
        text="Exportar excel"
        text-help="Exportar excel"
        aspect="secondary"
        onButtonClick={handleExportToExcel}
      >
        {' '}
      </RfUiButton>
      {showAuditButton && (
        <RfUiButton
          disabled={enableAudit}
          text="Auditar Selección"
          text-help="Auditar Selección"
          onButtonClick={auditarSeleccion}
        />
      )}
      {showClaimButton && (
        <RfUiButton
          disabled={enableClaim}
          text="Reclamar"
          text-help="Reclamar"
          onButtonClick={openModalCerrado}
        />
      )}
      {showValidButton && (
        <RfUiButton
          disabled={enableValid}
          text="Validar"
          text-help="Validar"
          onButtonClick={openModalCerradoValidar}
        />
      )}
      {showRejectButton && (
        <RfUiButton
          disabled={enableReject}
          text="Rechazar"
          text-help="Rechazar"
          onButtonClick={openModalRechazarCerrado}
        />
      )}
    </div>
  );
};

const CardTableContainer = ({
  isReportHours,
  headers,
  literalsHeaderTable,
  arrayData,
  list,
  hideAudit,
  canSeeUserInfo,
  showDetailLinkResponsive,
  showSelectList,
  redirect,
  handleCheckPractica,
  handleCellSelect,
  handleSort,
  dataCount,
  isTrue,
  handleCheckAll,
  itemsOnLeft,
  orderMobile,
  orderOptions,
  handleShoose,
}: any): ReactElement => (
  <div className="rf-container-card-table">
    <RfUiCardTable
      literals-header-table={getHeaderTable(isReportHours, headers, literalsHeaderTable)}
      literals-body-table={getBodyTable(isReportHours, arrayData, list, hideAudit, canSeeUserInfo)}
      extractLast={showDetailLinkResponsive}
      selectList={showSelectList}
      onNavigation={redirect}
      onCheckChange={(e: any) => {
        console.log('Cell Select');
        handleCheckPractica(e);
      }}
      onSelectChange={handleCellSelect}
      onSelectOrderChange={handleSort}
      selectAllTitle={getSelectAllTitle(dataCount, isTrue)}
      onCheckChangeAll={(e) => {
        console.log('Change ALL');
        handleCheckAll(e.detail.data);
      }}
      indexBreak={itemsOnLeft || 5}
      order={getOrderValue(dataCount, orderMobile)}
      optionsOrder={orderOptions}
      onShooseImg={(e) => handleShoose(e.detail.data)}
    />
  </div>
);

const DesktopTableContainer = ({
  headers,
  literalsHeaderTable,
  arrayData,
  handleSort,
  redirect,
  handleCheckPractica,
  handleCheckAll,
  handleCellSelect,
  handleShoose,
}: any): ReactElement => (
  <div className="rf-container-table">
    <RfCdkTable
      literals-header-table={JSON.stringify(headers || literalsHeaderTable)}
      literals-body-table={arrayData}
      title-table="Listado de prácticas"
      onSorting={handleSort}
      onNavigation={redirect}
      onCheckChange={(e: any) => {
        handleCheckPractica(e);
      }}
      onCheckChangeAll={(e) => {
        handleCheckAll(e.detail.data);
      }}
      onSelectChange={(e) => {
        handleCellSelect(e.detail);
      }}
      onShooseImg={(e) => handleShoose(e.detail.data)}
    />
  </div>
);

const TablePaginator = ({
  arrayDataIsEmpty,
  pages,
  handlePage,
  handleFilter,
  selectedPage,
  activePage,
  handlePaginationClick,
}: any): ReactElement | null => {
  if (!shouldShowPaginator(arrayDataIsEmpty, pages, handlePage)) {
    return null;
  }

  return (
    <div className="rf-paginator">
      {shouldShowMainPagination(handleFilter, pages) && (
        <RfUiPagination
          className={`adios ${selectedPage}, ${activePage}`}
          number-visible-pages={getVisiblePages()}
          number-of-pages={pages}
          active-page={selectedPage}
          page-selected={selectedPage}
          onPageSelected={handlePaginationClick}
        />
      )}

      {handleFilter && (
        <RfUiPagination
          className={`hola ${selectedPage}, ${activePage}`}
          number-visible-pages={getVisiblePages()}
          number-of-pages={handlePage}
          active-page={activePage}
          page-selected={activePage}
          onPageSelected={handlePaginationClick}
        />
      )}
    </div>
  );
};

const TableModals = ({
  canSeeClaimCheckField,
  canSeeValidButton,
  canSeeRejectCheckField,
  modalCerradoType,
  modalCerradoTypeValidar,
  modalCerradoRechazarType,
  closeModalCerrado,
  confirmModalCerrado,
  closeModalCerradoValidar,
  confirmModalCerradoValidar,
  closeModalRechazarCerrado,
  confirmModalRechazarCerrado,
  isObservacionesInstructorDisabled,
  observaciones_instructor,
  handleChangeObservacionesInstructor,
  profileCanRejectPractice,
  handleClickRejectPracticeButton,
}: any): ReactElement => (
  <>
    {canSeeClaimCheckField && (
      <RfUiModal
        title-modal={
          'Solo se reclamarán las prácticas con estado Reclamada o Pendiente de Validar'
        }
        id-modal="modalCursoCerrado"
        modal-type={modalCerradoType}
        onModalClose={closeModalCerrado}
        onConfirm={confirmModalCerrado}
        modalConfirmText="Aceptar"
      ></RfUiModal>
    )}
    {canSeeValidButton && (
      <RfUiModal
        title-modal={
          'Solo se validan las prácticas con estado Reclamada o Pendiente de Validar'
        }
        id-modal="modalCursoCerrado"
        modal-type={modalCerradoTypeValidar}
        onModalClose={closeModalCerradoValidar}
        onConfirm={confirmModalCerradoValidar}
        modalConfirmText="Aceptar"
      ></RfUiModal>
    )}
    {canSeeRejectCheckField && (
      <div>
        <RfUiModal
          title-Modal={'Indique un motivo de rechazo'}
          id-modal="empty"
          modal-type={modalCerradoRechazarType}
          slot-content={true}
          onModalClose={closeModalRechazarCerrado}
          onConfirm={() => {
            confirmModalRechazarCerrado(false);
          }}
        >
          <div slot="content">
            <ObservationsContainer
              title={language.informacionPractica.observacionesInstructorLabel}
              disableObservationAlumn={!isObservacionesInstructorDisabled}
              valueObservation={observaciones_instructor}
              handleObservationsChange={handleChangeObservacionesInstructor}
            />
          </div>
          <div slot="content">
            <RfUiButton
              disabled={!profileCanRejectPractice}
              text={language.informacionPractica.rechazarLabel}
              type="button"
              aspect="white"
              className="rf-button"
              onButtonClick={handleClickRejectPracticeButton}
            >
            </RfUiButton>
          </div>
        </RfUiModal>
      </div>
    )}
  </>
);

const Table = ({
  redirect,
  list,
  showSelectList,
  showButtonDetail,
  data,
  dataCount,
  headers,
  handleFilter,
  hideAudit,
  hideClaim,
  hideValid,
  hideReject,
  handleExportExcel,
  handleCellSelect,
  handleShoose,
  handleCheckAll,
  handleCheckPractica,
  pagination25, // Element only from "Informe de horas" -> true if it is 25 elements for page default
  orderMobile,
  itemsOnLeft,
  orderOptions,
  isReportHours = false,
}: any): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    practicesParse,
    elementsPerPage,
    practicasAuditoriaParse,
    practicasAuditoriaSeleccionadas,
    practicasReclamadaSeleccionadas,
    practicasRechazadaSeleccionadas,
    practicesValidSelect,
    observaciones_instructor,
    practicaSeleccionada,
    profileActive,
    isManagerProfile,
    isInstructorProfile,
    isFormadorProfile,
    isAdminGestorProfile,
    pages,
    selectedPage,
    practicas
  } = useSelector<any, any>(selectors);

  const defPagination = getDefaultPagination(pagination25);

  const [enableAudit, setEnableAudit] = useState(true);
  const [enableClaim, setEnableClaim] = useState(true);
  const [enableValid, setEnableValid] = useState(true);
  const [enableReject, setEnableReject] = useState(true);
  const [handlePage, setHandlePage] = useState(0);
  const savedPageActiveNumber = getSessionNumber('pageActive', '1');
  const [activePage, setActivePage] = useState(savedPageActiveNumber);
  const savedElemPage = getSessionNumber('elementsPerPage', defPagination);
  const [elemPage, setElemPage] = useState(savedElemPage);
  const [modalCerradoType, setModalCerradoType] = useState('');
  const [modalCerradoTypeValidar, setModalCerradoTypeValidar] = useState('');
  const [modalCerradoRechazarType, setModalCerradoRechazarType] = useState('');
  const [showDetailLinkResponsive] = useState(showButtonDetail);

  const canSee = useCan();

  const canSeeInstructor = hasAccess(profileActive, [userProfile.instructor]);

  const canSeeValidButton = hasAccess(profileActive, [
    userProfile.instructor,
    userProfile.administrador,
    //userProfile.gestor,
    userProfile.formador
  ]);

  const canSeeClaimCheckField = hasAccess(profileActive, [
    userProfile.gestor,
    userProfile.administrador,
    userProfile.formador,
  ]);

  const canSeeRejectCheckField = hasAccess(profileActive, [
    userProfile.gestor,
    userProfile.administrador,
    userProfile.formador,
  ]);

  const canSeeUserInfo = hasAccess(profileActive, [
    userProfile.gestor,
    userProfile.administrador,
    userProfile.formador,
    userProfile.supervisor,
    userProfile.visualizador,
  ]);

  const literalsHeaderTable: LiteralHeaderTable[] = useMemo(
    () => (list ? [] : getHeaderAudit()),
    [list, profileActive]
  );

  useEffect(() => {
    resetSelectedPractices(
      dispatch,
      practicasAuditoriaSeleccionadas,
      practicasReclamadaSeleccionadas,
      practicasRechazadaSeleccionadas,
      practicesValidSelect
    );
  }, [practicas]);

  useEffect(() => {
    const savedPageNumber = getSessionNumber('pageSelected', '1');
    if (savedPageNumber !== selectedPage) {
      dispatch(setSelectedPage(savedPageNumber));
    }

    const savedPageResults = getSessionNumber('elementsPerPage', defPagination);
    if (savedPageResults !== elemPage) {
      setElemPage(savedPageResults);
    }

    const savedPageActiveNumber = getSessionNumber('pageActive', '0');
    if (savedPageActiveNumber !== activePage) {
      setActivePage(savedPageActiveNumber);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    dispatch(setElementsPerPage(elemPage));
    dispatch(resetValidarPracticaRequest());
  }, [elemPage]);

  const filter = useRef({
    page: 1,
    elementsPerPage: elemPage,
    sortBy: '',
    sortingSort: '',
  });

  const arrayData = useMemo(() => {
    return buildArrayData(data, list, practicesParse, practicasAuditoriaParse);
  }, [data, list, practicesParse, practicasAuditoriaParse]);

  const newPagination = getPaginationSizes(pagination25);
  const arrayDataIsEmpty = useMemo(() => {
    return isJsonArrayEmpty(arrayData);
  }, [arrayData]);

  const pagination: string = useMemo(
    (): string => buildPagination(newPagination, pagination25),
    [elementsPerPage]
  );

  const isObservacionesInstructorDisabled: boolean = useMemo(() => {
    return true;
  }, [
    isManagerProfile,
    isFormadorProfile,
    isInstructorProfile,
    practicaSeleccionada,
  ]);

  const profileCanRejectPractice: boolean = useMemo(() => {
    return !!observaciones_instructor;
  }, [observaciones_instructor]);

  useEffect(() => {
    setEnableAudit(!hasSelectedItems(practicasAuditoriaSeleccionadas));
  }, [practicasAuditoriaSeleccionadas]);

  useEffect(() => {
    setEnableClaim(!hasSelectedItems(practicasReclamadaSeleccionadas));
  }, [practicasReclamadaSeleccionadas]);

  useEffect(() => {
    setEnableReject(
      shouldDisableReject(
        practicasRechazadaSeleccionadas,
        practicas,
        isAdminGestorProfile,
        isFormadorProfile
      )
    );
  }, [practicasRechazadaSeleccionadas, practicas, profileActive]);

  useEffect(() => {
    setEnableValid(shouldDisableValid(practicesValidSelect, practicas));
  }, [practicas, practicasRechazadaSeleccionadas, profileActive, practicesValidSelect]);

  const auditarSeleccion = () => {
    dispatch(postAuditoriaRequest());
    navigateToHomeAndAudit(history);
    dispatch(resetCreatePracticeForm());
  };

  const validSelection = () => {
    dispatch(postValidarPracticaRequest());
    navigateToAuditAndHome(history);
    dispatch(resetCreatePracticeForm());
  };

  const openModalCerrado = () => {
    setModalCerradoType('confirm');
  };

  const openModalCerradoValidar = () => {
    setModalCerradoTypeValidar('confirm');
  };

  const closeModalCerradoValidar = () => {
    setModalCerradoTypeValidar('');
  };

  const confirmModalCerradoValidar = () => {
    validSelection();
  };

  const closeModalCerrado = () => {
    setModalCerradoType('');
  };

  const openModalRechazarCerrado = () => {
    setModalCerradoRechazarType('empty');
  }

  const closeModalRechazarCerrado = () => {
    setModalCerradoRechazarType('');
  }

  const confirmModalCerrado = () => {
    reclamarSeleccion();
  };

  const reclamarSeleccion = () => {
    dispatch(postReclamarPracticaRequest());
    navigateToAuditAndHome(history);
    dispatch(resetCreatePracticeForm());
  };

  const handleChangeObservacionesInstructor = (e: string) => {
    dispatch(setObservacionesInstructor(e));
  };

  const confirmModalRechazarCerrado = () => {
    handleClickRejectPracticeButton();
  };

  const handleClickRejectPracticeButton = () => {
    dispatch(postRechazarPracticaRequest());
    navigateToAuditAndHome(history);
    dispatch(resetCreatePracticeForm());
  };

  const handleResultsNumberChange = (e: any) => {
    paginationChangeNumber(window.location.pathname, true);
    const elementsPerPage = parseInt(e.detail);

    sessionStorage.setItem(
      `elementsPerPage-${window.location.pathname}`,
      e.detail
    );
    setElemPage(elementsPerPage);

    if (handleFilter) {
      filter.current = { ...filter.current, elementsPerPage, page: 1 };
      calcNumberOfPages();
      handleFilter(parseFilter(filter.current));
    }
  };

  const handlePaginationClick = (e: CustomEvent) => {
    const inputs: any = document.querySelectorAll('.rf-checkbox__input');
    inputs.forEach((i) => (i.checked = false));
    resetSelectedPractices(
      dispatch,
      practicasAuditoriaSeleccionadas,
      practicasReclamadaSeleccionadas,
      practicasRechazadaSeleccionadas,
      practicesValidSelect
    );

    const page = e.detail;
    sessionStorage.setItem(`pageSelected-${window.location.pathname}`, page);
    sessionStorage.setItem(`pageActive-${window.location.pathname}`, page);
    dispatch(setSelectedPage(page));

    if (handleFilter) {
      filter.current = { ...filter.current, elementsPerPage: elemPage, page };
      setActivePage(page);
      handleFilter(parseFilter(filter.current));
    }

  };

  const handleSort = (e: CustomEvent): void => {
    const sort = {
      sortBy: getSortLabel(e.detail.order),
      sortingSort: e.detail.ascDesc,
    } as Sort;
    dispatch(setSort(sort));

    if (handleFilter) {
      filter.current = { ...filter.current, ...sort };
      handleFilter(parseFilter(filter.current));
    }
  };

  const calcNumberOfPages = () => {
    const savedPageActiveNumber = getSessionNumber('pageActive', '0');
    const savedPages = getSessionNumber('pages', '0');
    const pages = getCalculatedPages(dataCount, filter.current.elementsPerPage);

    if (pages !== Number(savedPages)) {
      setActivePage(getActivePageFromSavedState(pages, savedPageActiveNumber));
      sessionStorage.setItem(
        `pages-${window.location.pathname}`,
        pages.toString()
      );
    }
    setHandlePage(pages);
  };

  function handleExportToExcel(e: any) {
    e.preventDefault();
    if (handleExportExcel) {
      handleExportExcel();
    } else {
      dispatch(exportExcel());
    }
  }
  /*isTrue: variable for show title 'Seleccionar todo' in mobile. Only visible by canSeeInstructor || canSeeClaimCheckField,
     in Home and Audit
    */
  const isTrue =
    (canSeeInstructor || canSeeClaimCheckField || canSeeRejectCheckField) && !(hideAudit && !list);

  useEffect(() => {
    calcNumberOfPages();
  }, [dataCount]);

  return (
    <>
      <div
        className={`rf-flex rf-justify-space rf-align-center
        ${getResponsiveBlockClass()}`}
      >
        <RfUiSelectList
          className="card-table-result-content"
          label="Ver"
          second-label={`resultados por página de ${dataCount}`}
          options={pagination}
          // TODO - falta una propiedad en este elemento del ui-kit para seleccionar el valor
          onOptionActive={handleResultsNumberChange}
        />

        <TableActions
          canSeePerfil={canSee.canSeePerfil}
          enableAudit={enableAudit}
          enableClaim={enableClaim}
          enableValid={enableValid}
          enableReject={enableReject}
          handleExportToExcel={handleExportToExcel}
          auditarSeleccion={auditarSeleccion}
          openModalCerrado={openModalCerrado}
          openModalCerradoValidar={openModalCerradoValidar}
          openModalRechazarCerrado={openModalRechazarCerrado}
          hideAudit={hideAudit}
          hideClaim={hideClaim}
          hideValid={hideValid}
          hideReject={hideReject}
          list={list}
        />
      </div>

      <CardTableContainer
        isReportHours={isReportHours}
        headers={headers}
        literalsHeaderTable={literalsHeaderTable}
        arrayData={arrayData}
        list={list}
        hideAudit={hideAudit}
        canSeeUserInfo={canSeeUserInfo}
        showDetailLinkResponsive={showDetailLinkResponsive}
        showSelectList={showSelectList}
        redirect={redirect}
        handleCheckPractica={handleCheckPractica}
        handleCellSelect={handleCellSelect}
        handleSort={handleSort}
        dataCount={dataCount}
        isTrue={isTrue}
        handleCheckAll={handleCheckAll}
        itemsOnLeft={itemsOnLeft}
        orderMobile={orderMobile}
        orderOptions={orderOptions}
        handleShoose={handleShoose}
      />

      <DesktopTableContainer
        headers={headers}
        literalsHeaderTable={literalsHeaderTable}
        arrayData={arrayData}
        handleSort={handleSort}
        redirect={redirect}
        handleCheckPractica={handleCheckPractica}
        handleCheckAll={handleCheckAll}
        handleCellSelect={handleCellSelect}
        handleShoose={handleShoose}
      />

      {arrayDataIsEmpty && (
        <p className="table-no-data-message">No hay resultados</p>
      )}

      <TablePaginator
        arrayDataIsEmpty={arrayDataIsEmpty}
        pages={pages}
        handlePage={handlePage}
        handleFilter={handleFilter}
        selectedPage={selectedPage}
        activePage={activePage}
        handlePaginationClick={handlePaginationClick}
      />

      <TableModals
        canSeeClaimCheckField={canSeeClaimCheckField}
        canSeeValidButton={canSeeValidButton}
        canSeeRejectCheckField={canSeeRejectCheckField}
        modalCerradoType={modalCerradoType}
        modalCerradoTypeValidar={modalCerradoTypeValidar}
        modalCerradoRechazarType={modalCerradoRechazarType}
        closeModalCerrado={closeModalCerrado}
        confirmModalCerrado={confirmModalCerrado}
        closeModalCerradoValidar={closeModalCerradoValidar}
        confirmModalCerradoValidar={confirmModalCerradoValidar}
        closeModalRechazarCerrado={closeModalRechazarCerrado}
        confirmModalRechazarCerrado={confirmModalRechazarCerrado}
        isObservacionesInstructorDisabled={isObservacionesInstructorDisabled}
        observaciones_instructor={observaciones_instructor}
        handleChangeObservacionesInstructor={handleChangeObservacionesInstructor}
        profileCanRejectPractice={profileCanRejectPractice}
        handleClickRejectPracticeButton={handleClickRejectPracticeButton}
      />
    </>
  );
};

export default Table;
